import { decamelizeKeys } from 'humps'
import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { getCurrentClubId } from './network'
import { apolloClient } from '@fe/business/src/apollo'

import { actions, types, selectors as gallerySelectors } from '../reducers/gallery'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

const CLUB_GALLERY_IMAGES_QUERY = gql`
  query ClubGalleryImages($clubId: ID!) {
    ClubQuery {
      clubGalleryImages(clubId: $clubId) {
        id
        clubId
        fileName
        order
        createdAt
        updatedAt
      }
    }
  }
`

// GraphQL IDs serialize as strings - coerce back to numbers to match the pre-existing REST contract
const toReduxGalleryImage = image => _.flow(
  decamelizeKeys,
  _.update('id', Number),
  _.update('club_id', Number)
)(image)

export function * loadGalleryEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_GALLERY_IMAGES_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    // resolver returns null (not []) when there are no images
    const images = _.get('data.ClubQuery.clubGalleryImages', response) || []
    return _.map(toReduxGalleryImage, images)
  } catch (error) {
    return { error }
  }
}

const UPLOAD_CLUB_GALLERY_IMAGE_MUTATION = gql`
  mutation UploadClubGalleryImage($input: UploadClubGalleryImageInput!) {
    ClubMutation {
      uploadClubGalleryImage(input: $input) {
        id
        clubId
        fileName
        order
        createdAt
        updatedAt
      }
    }
  }
`

export function * uploadImageEffect (file) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPLOAD_CLUB_GALLERY_IMAGE_MUTATION,
      variables: { input: { clubId, image: file } }
    })
    const image = _.get('data.ClubMutation.uploadClubGalleryImage', response)
    return toReduxGalleryImage(image)
  } catch (error) {
    return { error }
  }
}

const DELETE_CLUB_GALLERY_IMAGE_MUTATION = gql`
  mutation DeleteClubGalleryImage($clubId: ID!, $imageId: ID!) {
    ClubMutation {
      deleteClubGalleryImage(clubId: $clubId, imageId: $imageId)
    }
  }
`

export function * deleteImageEffect (imageId) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: DELETE_CLUB_GALLERY_IMAGE_MUTATION,
      variables: { clubId, imageId }
    })
    const success = _.get('data.ClubMutation.deleteClubGalleryImage', response)
    if (success) {
      yield put(actions.purgeImage({ imageId }))
    }
  } catch (error) {
    // purgeImage intentionally skipped on error, matching previous REST behavior
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadGalleryFlow ({ payload }) {
  const gallery = yield call(loadGalleryEffect, payload)

  if (gallery.error) {
    yield put(actions.loadError(gallery))
  } else {
    yield put(actions.loadSuccess(gallery))
  }
}

export function * uploadImageFlow ({ payload }) {
  yield put(actions.setBlocked({ blocked: true }))
  const result = yield uploadImageEffect(payload)
  yield put(actions.addGalleryImage(result))
  yield put(actions.setBlocked({ blocked: false }))
  yield put(actions.uiDialogUploadHide())
}

export function * deleteSelectedFlow () {
  const deleteArray = yield select(gallerySelectors.getDeleteArray)
  if (_.isEmpty(deleteArray)) {
    return
  }
  yield put(actions.setBlocked({ blocked: true }))
  yield _.map(id => call(deleteImageEffect, id), deleteArray)
  yield put(actions.setBlocked({ blocked: false }))
}

export default function * () {
  return yield all([
    takeLatest(types.LOAD_REQUEST, loadGalleryFlow),
    takeLatest(types.UPLOAD_IMAGE, uploadImageFlow),
    takeLatest(types.DELETE_SELECTED, deleteSelectedFlow)
  ])
}
